import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { toast } from '@/store/toastStore'
import type { AttestationMutation, SectionAttestationState } from '@/types/returnCase.types'

export interface AttestationClient {
  attestation: (caseId: string, sectionKey: string) => Promise<SectionAttestationState>
  confirmSection?: (
    caseId: string,
    sectionKey: string,
    input: Record<string, unknown>
  ) => Promise<AttestationMutation>
  verifySection?: (
    caseId: string,
    sectionKey: string,
    input: Record<string, unknown>
  ) => Promise<AttestationMutation>
  confirmHighRiskItem?: (
    caseId: string,
    sectionKey: string,
    itemKey: string,
    input: Record<string, unknown>
  ) => Promise<AttestationMutation>
}

interface Props {
  caseId: string
  sectionKeys: string[]
  client: AttestationClient
  mode: 'customer' | 'staff'
  canAct: boolean
  onChanged?: () => Promise<void> | void
}

const stateClass = (state: string) =>
  state === 'CURRENT'
    ? 'badge badge-success'
    : state === 'STALE' || state === 'REJECTED' || state === 'RETURNED'
      ? 'badge badge-warning'
      : 'badge'

export default function SectionAttestationPanel({
  caseId,
  sectionKeys,
  client,
  mode,
  canAct,
  onChanged,
}: Props) {
  const keys = sectionKeys.length ? sectionKeys : ['case']
  const [sectionKey, setSectionKey] = useState(keys[0])
  const [reason, setReason] = useState('')
  const queryClient = useQueryClient()
  const queryKey = ['section-attestation', mode, caseId, sectionKey]
  const state = useQuery({
    queryKey,
    queryFn: () => client.attestation(caseId, sectionKey),
  })
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey })
    await onChanged?.()
  }
  const action = useMutation({
    mutationFn: async (decision: string) => {
      if (!state.data) throw new Error('Section state has not loaded')
      if (reason.trim().length < 10) throw new Error('Enter a reason of at least 10 characters')
      const common = {
        expected_revision: state.data.case_revision,
        displayed_hash: state.data.manifest.manifest_hash,
        decision,
        wording_version: '1.0',
        reason: reason.trim(),
      }
      if (mode === 'customer' && client.confirmSection)
        return client.confirmSection(caseId, sectionKey, common)
      if (mode === 'staff' && client.verifySection)
        return client.verifySection(caseId, sectionKey, { ...common, exceptions: [] })
      throw new Error('This session is not allowed to perform the requested action')
    },
    onSuccess: async () => {
      setReason('')
      await refresh()
      toast.success(mode === 'customer' ? 'Section response recorded' : 'Verification recorded')
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Action rejected',
        error instanceof Error ? error.message : 'Reload the section and review its current state.'
      )
    },
  })
  const highRisk = useMutation({
    mutationFn: async (item: SectionAttestationState['high_risk_items'][number]) => {
      if (!state.data || !client.confirmHighRiskItem)
        throw new Error('High-risk confirmation is not available')
      return client.confirmHighRiskItem(caseId, sectionKey, item.item_key, {
        expected_revision: state.data.case_revision,
        displayed_manifest_hash: state.data.manifest.manifest_hash,
        item_version: item.item_version,
        item_hash: item.item_hash,
        decision: 'CONFIRMED',
        wording_version: '1.0',
        reason: `Principal reviewed ${item.safe_label}`,
      })
    },
    onSuccess: refresh,
    onError: async error => {
      await refresh()
      toast.error(
        'Item confirmation rejected',
        error instanceof Error ? error.message : 'Reload and review the changed item.'
      )
    },
  })

  return (
    <section className="card mt-6 p-5" aria-labelledby={`${mode}-attestation-heading`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${mode}-attestation-heading`} className="font-semibold">
            {mode === 'customer' ? 'Review and confirm a section' : 'Section verification'}
          </h2>
          <p className="mt-1 text-sm text-text-secondary">
            Actions apply only to the displayed immutable section version. Changes make prior
            actions stale; they do not silently remain checked.
          </p>
        </div>
        <label className="text-sm">
          <span className="sr-only">Section</span>
          <select
            className="input"
            value={sectionKey}
            onChange={event => setSectionKey(event.target.value)}
          >
            {keys.map(key => (
              <option key={key} value={key}>
                {key.replace(/-/g, ' ')}
              </option>
            ))}
          </select>
        </label>
      </div>

      {state.isLoading && <p className="mt-4">Loading current section version…</p>}
      {state.isError && (
        <div className="alert alert-error mt-4" role="alert">
          The current section version could not be loaded. No action was recorded.
        </div>
      )}
      {state.data && (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 rounded border border-border p-4 text-sm sm:grid-cols-2">
            <div>
              <span className="text-text-secondary">Customer confirmation</span>
              <div className="mt-1">
                <span className={stateClass(state.data.customer_state)}>
                  {state.data.customer_state}
                </span>
              </div>
            </div>
            <div>
              <span className="text-text-secondary">Professional verification</span>
              <div className="mt-1">
                <span className={stateClass(state.data.professional_state)}>
                  {state.data.professional_state}
                </span>
              </div>
            </div>
            <div className="sm:col-span-2">
              <span className="text-text-secondary">Current section hash</span>
              <code className="mt-1 block break-all text-xs">
                {state.data.manifest.manifest_hash}
              </code>
            </div>
          </div>

          {state.data.changed_inputs.length > 0 && (
            <div className="alert alert-warning" role="status">
              <strong>Why a previous action is stale</strong>
              <ul className="mt-2 list-disc pl-5 text-sm">
                {state.data.changed_inputs.map(item => (
                  <li key={`${item.input_kind}:${item.input_key}`}>
                    {item.change.toLowerCase()} {item.input_kind.toLowerCase()} {item.input_key}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {state.data.blockers.length > 0 && (
            <div className="rounded border border-warning-500 p-3" role="status">
              <strong>Complete before confirmation</strong>
              <ul className="mt-2 list-disc pl-5 text-sm">
                {state.data.blockers.map(item => (
                  <li key={`${item.code}:${item.message}`}>{item.message}</li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'customer' && state.data.high_risk_items.length > 0 && (
            <div>
              <h3 className="font-medium">Items requiring explicit confirmation</h3>
              <div className="mt-2 space-y-2">
                {state.data.high_risk_items.map(item => (
                  <div key={item.item_key} className="rounded border border-border p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <strong>{item.safe_label}</strong>
                      <span className={stateClass(item.state)}>{item.state}</span>
                    </div>
                    <p className="mt-2">{item.wording}</p>
                    {canAct && item.state !== 'CURRENT' && (
                      <button
                        className="btn btn-secondary mt-3"
                        type="button"
                        disabled={highRisk.isPending}
                        onClick={() => highRisk.mutate(item)}
                      >
                        Confirm this item
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {canAct && (
            <div className="rounded border border-border p-4">
              <p className="text-sm">
                {mode === 'customer'
                  ? 'I reviewed this information and, to the best of my knowledge, it is complete and correct.'
                  : 'I reviewed the routing, supporting evidence, reconciliations, exceptions and tax treatment for this section using the identified rule pack.'}
              </p>
              <label className="mt-3 block text-sm">
                Reason
                <textarea
                  className="input mt-1 min-h-20 w-full"
                  value={reason}
                  minLength={10}
                  maxLength={1000}
                  onChange={event => setReason(event.target.value)}
                />
              </label>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={action.isPending || state.data.blockers.length > 0}
                  onClick={() => action.mutate(mode === 'customer' ? 'CONFIRMED' : 'VERIFIED')}
                >
                  {mode === 'customer' ? 'Confirm current section' : 'Verify current section'}
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  disabled={action.isPending}
                  onClick={() => action.mutate(mode === 'customer' ? 'REJECTED' : 'RETURNED')}
                >
                  {mode === 'customer' ? 'Reject current section' : 'Return current section'}
                </button>
              </div>
            </div>
          )}

          {state.data.history.length > 0 && (
            <details>
              <summary className="cursor-pointer font-medium">
                Prior actions and audit history
              </summary>
              <ul className="mt-2 space-y-2 text-sm">
                {state.data.history.map(item => (
                  <li key={item.id} className="rounded border border-border p-3">
                    {item.actor_kind} · {item.decision} · {item.state} ·{' '}
                    {new Date(item.created_at).toLocaleString()} · revision {item.case_revision}
                    <div className="mt-1 text-text-secondary">{item.reason}</div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </section>
  )
}
